import { Table } from 'antd';
import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import type { ColumnProps } from 'antd/lib/table';
import type { FilterValue } from 'common/models';
import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import { CustomEmpty } from 'components';
import { unitShortcut } from 'helpers/materialHelpers';
import { valueCurrencyShortcut } from 'helpers/priceHelpers';
import { getColumnsToRender } from 'helpers/renderSettings';
import { getSortableColumnProps } from 'helpers/tableHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { MaterialAtStore, StoreMaterialTableQuery } from 'pages/material/models';
import { useGetCodebooksQuery } from 'pages/settings/codebooks/queries';
import React, { useState } from 'react';

interface Props {
  isLoading: boolean;
  data: MaterialAtStore[];
  pagination: TablePaginationConfig;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Partial<Record<string, FilterValue | null>>,
    sorter: SorterResult<MaterialAtStore>
  ) => void;
  selectedTableColumns: string[];
  tableParams: StoreMaterialTableQuery;
}

const StoreMaterialTable = (props: Props) => {
  const { isLoading, data, pagination, onTableChange, selectedTableColumns, tableParams } = props;
  const { t } = useCustomTranslation();

  const codebooksQuery = useGetCodebooksQuery();
  const companyAndUserQuery = useGetCompanyAndUserInfoQuery();
  const companySettings = companyAndUserQuery.data?.company;

  const [expandedRowKeys, setExpandedRowKeys] = useState(['']);

  const currencyId = companySettings ? companySettings.currencyId : '';

  const columns: Array<ColumnProps<MaterialAtStore>> = [
    {
      title: t('material.table.name'),
      dataIndex: 'name',
      ...getSortableColumnProps('name', tableParams),
      className: 'bold'
    },
    {
      title: t('misc.categoryTitle'),
      dataIndex: 'materialCategoryId',
      ...getSortableColumnProps('materialCategoryName', tableParams),
      filters: codebooksQuery.data?.materialCategories.map((x) => ({ text: x.name, value: x.id })),
      filteredValue: tableParams.materialCategoryId?.split(',') || null,
      render: (text, record) => {
        const category = codebooksQuery.data?.materialCategories.find(
          (x) => x.id === record.materialCategoryId
        );

        return <span>{category ? category.name : ''}</span>;
      }
    },
    {
      title: t('material.table.price'),
      dataIndex: 'price',
      ...getSortableColumnProps('price', tableParams),
      render: (text, record) => valueCurrencyShortcut(record.price || 0, currencyId, t)
    },
    {
      title: t('material.table.costPrice'),
      dataIndex: 'costPrice',
      ...getSortableColumnProps('costPrice', tableParams),
      render: (text, record) => valueCurrencyShortcut(record.costPrice || 0, currencyId, t)
    },
    {
      title: t('misc.amountsTitle'),
      dataIndex: 'amount',
      ...getSortableColumnProps('amount', tableParams),
      render: (text, record) => {
        return record.amount !== undefined && record.amount !== null
          ? `${record.amount} ${t(unitShortcut(record.materialUnit))}`
          : t('misc.unlimited');
      }
    },
    {
      title: t('material.table.identificationNumber'),
      dataIndex: 'identificationNumber',
      ...getSortableColumnProps('identificationNumber', tableParams)
    },
    {
      title: t('material.table.supplierCode'),
      dataIndex: 'supplierCode',
      ...getSortableColumnProps('supplierCode', tableParams)
    },
    {
      title: t('material.table.supplierName'),
      dataIndex: 'materialSupplierId',
      ...getSortableColumnProps('materialSupplierName', tableParams),
      filters: codebooksQuery.data?.materialSuppliers.map((x) => ({ text: x.name, value: x.id })),
      filteredValue: tableParams.materialSupplierId?.split(',') || null,
      render: (text, record) => {
        const supplier = codebooksQuery.data?.materialSuppliers.find(
          (x) => x.id === record.materialSupplierId
        );

        return <span>{supplier ? supplier.name : ''}</span>;
      }
    },
    {
      title: t('material.table.externalId'),
      dataIndex: 'externalId',
      ...getSortableColumnProps('externalId', tableParams)
    }
  ];

  const expandedRow = (record: MaterialAtStore) => {
    return (
      <div className="MaterialTable_ExpandedRow">
        <div className="description">{record.description}</div>
      </div>
    );
  };

  const columnsToRender = getColumnsToRender(columns, selectedTableColumns);

  return (
    <Table
      locale={{
        emptyText: <CustomEmpty description={t('stores.emptyStoreText')} />
      }}
      loading={isLoading}
      dataSource={data}
      rowKey="id"
      columns={columnsToRender}
      className="PageTable MaterialTable clickable"
      expandable={{
        expandedRowRender: (record) => (record.description ? expandedRow(record) : null),
        expandRowByClick: true,
        onExpand: (expanded, record) =>
          expanded ? setExpandedRowKeys([record.id]) : setExpandedRowKeys([]),
        expandedRowKeys: expandedRowKeys
      }}
      rowClassName={(record) => (record.description ? '' : 'hiddenExpand')}
      onChange={(pagination, filters, sorter) =>
        onTableChange(pagination, filters as any, sorter as SorterResult<MaterialAtStore>)
      }
      pagination={pagination}
      scroll={{ x: true }}
    />
  );
};

export default StoreMaterialTable;
